import { t } from '@lingui/core/macro';
import { type OrganizationRecordMinimal, UserGroupMemberRole, UserGroupType } from '@navio/nsdk';
import type { UserSessionData } from 'noddi-web';

export type SelectableUserGroup = UserSessionData['user']['userGroups'][number];

type UserGroupKindFields = Pick<SelectableUserGroup, 'isPersonal' | 'isOrganization' | 'type'>;

/**
 * A leasing company or car dealership is a booking identity only for its employees.
 * Anyone else who is still a member stays on their own account, where the leased car lives.
 */
const canRepresentUserGroup = (userGroup: SelectableUserGroup) => {
  if (userGroup.holdsExternalCars && userGroup.memberRole !== UserGroupMemberRole.EMPLOYEE) {
    return false;
  }
  return userGroup.isPersonal || userGroup.isOrganization;
};

/** Only personal and organization user groups are valid contexts in the customer app. */
export const getSelectableUserGroups = (userData: UserSessionData) =>
  userData.user.userGroups.filter(canRepresentUserGroup);

export const isUserGroupOrganizationContext = (userGroup: UserGroupKindFields | undefined | null) => {
  if (!userGroup || userGroup.isPersonal || userGroup.type === UserGroupType.PERSONAL) {
    return false;
  }
  return (
    userGroup.isOrganization || userGroup.type === UserGroupType.ORGANIZATION || userGroup.type === UserGroupType.GROUP
  );
};

/** One line under a profile card: what the user can do there, which for an organization depends on the role. */
export const getUserGroupCardDescription = (
  userGroup: Pick<SelectableUserGroup, 'isPersonal' | 'isOrganization' | 'type' | 'isAdmin' | 'holdsExternalCars'>
) => {
  if (!isUserGroupOrganizationContext(userGroup)) {
    return t`Manage bookings for your personal vehicles`;
  }
  if (userGroup.holdsExternalCars) {
    return t`Manage the company's own cars and bookings`;
  }
  return userGroup.isAdmin
    ? t`Manage your organization's fleet, drivers and bookings`
    : t`Book and follow up on the cars you drive`;
};

/** The two workspace kinds a customer can act as. */
export type UserGroupSelectionContext = 'organization' | 'personal';

/**
 * Who a booking proposal is for. An organization proposal booked from a personal
 * profile lands the customer in the wrong workspace, so the login step needs this
 * before the customer is asked anything — `organization` on the record is the
 * same discriminant the checkout gates use, and analytics must not pick another.
 */
export const getProposalUserGroupContext = (
  organization: OrganizationRecordMinimal | null | undefined
): UserGroupSelectionContext => (organization ? 'organization' : 'personal');

/**
 * The group to pick without asking. A flow that owns its group (a booking proposal)
 * resolves to that membership or to nothing; otherwise only a single selectable
 * group is picked for the customer.
 */
export const resolveAutoSelectedUserGroup = <T extends Pick<SelectableUserGroup, 'id'>>(
  selectableUserGroups: T[],
  flowUserGroupId: number | undefined
): T | undefined => {
  if (flowUserGroupId != null) {
    return selectableUserGroups.find((userGroup) => userGroup.id === flowUserGroupId);
  }
  return selectableUserGroups.length === 1 ? selectableUserGroups[0] : undefined;
};

/**
 * The only selectable group, while the session does not act as it yet. Compared with the session
 * id PrivateRoute gates on, not the `isSelected` flag: the flag can be set while the id is missing,
 * and redirecting home then bounces straight back to the picker, forever.
 */
export const getOnlyUserGroupToSelect = <T extends Pick<SelectableUserGroup, 'id'>>(
  selectableUserGroups: T[],
  currentUserGroupId: number | undefined
): T | undefined => {
  const onlyUserGroup = resolveAutoSelectedUserGroup(selectableUserGroups, undefined);
  return onlyUserGroup?.id === currentUserGroupId ? undefined : onlyUserGroup;
};
